/**
 * Visitor-facing voice state indicator.
 *
 * Reads `voiceState` (signal) and renders a small status line —
 * "Connecting…", "Listening", "Speaking", "Thinking…", "Reconnecting…",
 * or the error message. Mounts as a Preact island next to the existing
 * voice toggle in the chat panel header.
 *
 * Designed for clarity over polish — Phase 1 ships testable voice;
 * follow-up iterations refine the visual treatment (per-state colors,
 * animations) per docs/archive/voice-widget-ux-delta.md.
 */

import { useComputed } from '@preact/signals';
import { isVoiceActive, voiceState } from '../voice-state';

const LABELS = {
  idle: '',
  connecting: 'Connecting…',
  listening: 'Listening',
  thinking: 'Thinking…',
  speaking: 'Speaking',
  interrupted: '…',
  degraded: 'Reconnecting…',
  error: 'Voice unavailable',
} as const;

export function VoiceStatusBadge() {
  // useComputed memoizes per-tick. We read .name + .errorMessage so
  // re-render only fires when those change, not every audio-level tick.
  const display = useComputed(() => {
    const s = voiceState.value;
    if (s.name === 'error' && s.errorMessage) return s.errorMessage;
    return LABELS[s.name];
  });
  const tone = useComputed(() => voiceState.value.name);

  if (!isVoiceActive.value) return null;

  return (
    <div
      class={`sp-voice-status sp-voice-status--${tone.value}`}
      role="status"
      aria-live="polite"
    >
      <span class="sp-voice-status__dot" />
      <span class="sp-voice-status__text">{display.value}</span>
    </div>
  );
}
